/** Margins used by Probability / Histogram SVG layout (must stay in sync with chart components). */
export const CHART_PLOT_MARGIN = { top: 60, bottom: 50, left: 60, right: 30 };

/** Carpet strip + axis label room above/below the plot area in probability/histogram charts. */
const COL1_SLOT_CHROME_PX = 80;

/**
 * Normalize layout class strings like "col1 single-chart" to the base grid token.
 */
export function normalizeChartLayoutClass(className?: string | null): string {
  const n = (className || "").trim().toLowerCase();
  if (n.startsWith("col2x2") || n.startsWith("col2")) {return "col2";}
  if (n.startsWith("col3x") || n.startsWith("col3")) {return "col3";}
  if (n.startsWith("col4")) {return "col4";}
  if (n.startsWith("col1")) {return "col1";}
  return n;
}

/**
 * Scroll/grid slot used to size a single-column chart on explore pages.
 */
export function findChartLayoutSlotElement(
  layoutAnchor?: HTMLElement | null
): HTMLElement | null {
  if (!layoutAnchor) {return null;}
  const scrollInner = layoutAnchor.closest(".performance-charts-scroll-inner");
  if (scrollInner instanceof HTMLElement) {return scrollInner;}
  const grid = layoutAnchor.closest(".col1.single-chart");
  if (grid instanceof HTMLElement) {return grid;}
  return null;
}

/**
 * Viewport-based plot height for a single-column chart.
 * Prefers the bounded scroll viewport on explore pages (fluid width > 1620px).
 */
export function col1PlotHeightPx(
  margins: { top: number; bottom: number } = CHART_PLOT_MARGIN,
  layoutAnchor?: HTMLElement | null
): number {
  const slotEl = findChartLayoutSlotElement(layoutAnchor);
  const slotHeight = slotEl?.clientHeight ?? 0;

  if (slotHeight > 240) {
    return Math.max(
      280,
      slotHeight - margins.top - margins.bottom - COL1_SLOT_CHROME_PX
    );
  }

  const vh = window.innerHeight || 800;
  const plotSlot = Math.max(480, vh * 0.82 - 100);
  return plotSlot - margins.top - margins.bottom;
}

/** Column count implied by a normalized grid layout token (col1..col4). */
function columnsForLayoutClass(className?: string | null): number {
  switch (normalizeChartLayoutClass(className)) {
    case "col4":
      return 4;
    case "col3":
      return 3;
    case "col2":
      return 2;
    default:
      return 1;
  }
}

/** Grid container (.col1/.col2/.col3/.col3x/.col2x2/.col4) that owns a chart's slot. */
function findChartGridElement(layoutAnchor?: HTMLElement | null): HTMLElement | null {
  if (!layoutAnchor) {return null;}
  const grid = layoutAnchor.closest(".col1, .col2, .col3, .col3x, .col2x2, .col4");
  return grid instanceof HTMLElement ? grid : null;
}

/**
 * Viewport-based plot height for a chart in a (possibly multi-row) grid.
 *
 * Divides the bounded scroll viewport among the grid's rows so that, e.g., a
 * 6-chart 3-column grid renders two rows that each take ~50% of the height,
 * instead of every chart claiming the full viewport (which pushed the second
 * row off-screen). Falls back to single-row behaviour when the grid can't be
 * measured.
 */
export function gridPlotHeightPx(
  margins: { top: number; bottom: number } = CHART_PLOT_MARGIN,
  layoutAnchor?: HTMLElement | null,
  layoutClass?: string | null
): number {
  const grid = findChartGridElement(layoutAnchor);
  const columns = columnsForLayoutClass(layoutClass ?? grid?.className);
  const chartCount = grid?.children?.length ?? 1;
  const rows = Math.max(1, Math.ceil(chartCount / Math.max(1, columns)));

  const singleRowHeight = col1PlotHeightPx(margins, layoutAnchor);
  if (rows <= 1) {
    return singleRowHeight;
  }

  // Re-derive the total viewport slot from the single-row height, then split
  // it across the rows (accounting for per-row chrome + inter-row gap).
  const slotHeight = singleRowHeight + margins.top + margins.bottom + COL1_SLOT_CHROME_PX;
  const perRowSlot = slotHeight / rows;
  return Math.max(220, perRowSlot - margins.top - margins.bottom - COL1_SLOT_CHROME_PX);
}
